import React, { Component } from 'react';
import { Navigate } from 'react-router-dom';
import LogReactError from '/src/services/Logging/LogReactError';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      originalUrl: null
    };
  }

  static getDerivedStateFromError(error) {
    console.log("getDerivedStateFromError called with:", error);
    return { 
      hasError: true,
      originalUrl: window.location.href
    };
  }

  componentDidCatch(error, errorInfo) {
    console.log("componentDidCatch called with:", error);
    console.log("Error info:", errorInfo);
    
    // Enhanced error information
    const enhancedErrorInfo = {
      ...errorInfo,
      // Add additional context
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      buildInfo: {
        mode: import.meta.env.MODE,
        version: import.meta.env.VITE_APP_VERSION || 'unknown',
        buildTime: import.meta.env.VITE_BUILD_TIME || 'unknown'
      },
      // Extract more readable component names from the stack
      componentStack: this.parseComponentStack(errorInfo.componentStack),
      // Add error type and message
      errorType: error.constructor.name,
      errorMessage: error.message,
      // Add stack trace if available
      stack: error.stack
    };
    
    // Only log the error once when we have the actual error details
    LogReactError(
      this.state.originalUrl || window.location.href,
      error?.toString() || 'Unknown error',
      enhancedErrorInfo
    );
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      hasError: true
    });
  }

  // Helper method to parse component stack and extract more readable information
  parseComponentStack(componentStack) {
    if (!componentStack) return null;
    
    try {
      // Split the stack into lines and try to extract meaningful information
      const lines = componentStack.split('\n').filter(line => line.trim());
      
      return lines.map(line => {
        // Try to extract component name and location
        const match = line.match(/at\s+(\w+)\s+\((.+)\)/);
        if (match) {
          return {
            component: match[1],
            location: match[2],
            originalLine: line.trim()
          };
        }
        
        // If no match, return the original line
        return {
          component: 'Unknown',
          location: 'Unknown',
          originalLine: line.trim()
        };
      });
    } catch (parseError) {
      console.error('Error parsing component stack:', parseError);
      return componentStack;
    }
  }

  render() {
    if (this.state.hasError) {
      console.log("Attempting to navigate to error page...");
      return <Navigate to="/error" replace />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
